import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { KTIcon } from "../../../_metronic/helpers";
import { API_URL } from "../../config/api.config";
import changelog from "../../data/changelog.json";
import "./WhatsNew.scss";

type ChangeType = "Enhancement" | "Bug Fix" | string;

interface ChangeItem {
  type: ChangeType;
  description: string;
}

interface Release {
  version: string;
  date: string;
  releasedAt?: string;
  changes: ChangeItem[];
}

interface Changelog {
  unreleased: ChangeItem[];
  releases: Release[];
}

const data = changelog as Changelog;

// Number of releases shown per page in the popup.
const RELEASES_PER_PAGE = 3;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Parse a YYYY-MM-DD string without timezone drift and format as "21 Jun 2026".
const formatDate = (iso: string): string => {
  const parts = (iso || "").split("-");
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

// "40 mins back", "2 hours back", "3 days back". Accepts a full ISO timestamp
// (preferred) or a plain YYYY-MM-DD date.
const formatRelative = (input: string): string => {
  if (!input) return "";
  const then =
    input.length <= 10
      ? (() => {
          const [y, m, d] = input.split("-").map((p) => parseInt(p, 10));
          return new Date(y, (m || 1) - 1, d || 1);
        })()
      : new Date(input);
  const ms = Date.now() - then.getTime();
  if (isNaN(ms)) return "";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} back`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} back`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} back`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} back`;
  const yrs = Math.floor(days / 365);
  return `${yrs} year${yrs === 1 ? "" : "s"} back`;
};

const typeStyle = (
  type: ChangeType
): { badge: string; icon: string; iconWrap: string; iconColor: string } => {
  if (type === "Bug Fix") {
    return { badge: "badge-light-warning", icon: "shield-tick", iconWrap: "bg-light-warning", iconColor: "text-warning" };
  }
  if (type === "Enhancement") {
    return { badge: "badge-light-primary", icon: "arrow-up-right", iconWrap: "bg-light-primary", iconColor: "text-primary" };
  }
  return { badge: "badge-light-info", icon: "information-5", iconWrap: "bg-light-info", iconColor: "text-info" };
};

const ChangeRow = ({ change }: { change: ChangeItem }) => {
  const s = typeStyle(change.type);
  return (
    <div className="whats-new-change">
      <span className={`whats-new-change__icon ${s.iconWrap}`}>
        <KTIcon iconName={s.icon} iconType="solid" className={`fs-5 ${s.iconColor}`} />
      </span>
      <div className="whats-new-change__text">
        <span className={`badge ${s.badge} fw-semibold me-2`}>{change.type}</span>
        {change.description}
      </div>
    </div>
  );
};

const ReleaseBlock = ({
  title,
  versionLabel,
  changes,
  unreleased = false,
}: {
  title: string;
  versionLabel?: string;
  changes: ChangeItem[];
  unreleased?: boolean;
}) => (
  <div className={`whats-new-release${unreleased ? " is-unreleased" : ""}`}>
    <span className="whats-new-release__dot" />
    <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
      <span className="whats-new-release__date">{title}</span>
      {versionLabel && (
        <span className="badge badge-light-primary fw-semibold">{versionLabel}</span>
      )}
    </div>
    {changes.map((c, i) => (
      <ChangeRow key={i} change={c} />
    ))}
  </div>
);

const WhatsNew = () => {
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(0);
  const [appVersion, setAppVersion] = useState<string | null>(null);

  // The installed app version (same source as the footer). The changelog's own
  // release versions are just historical labels and can lag behind the build.
  useEffect(() => {
    fetch(`${API_URL}/api/setup/version`)
      .then((res) => res.json())
      .then((data) => {
        if (data.version) setAppVersion(data.version);
      })
      .catch(() => {});
  }, []);

  const releases = useMemo(
    () =>
      // Sort newest-first by the full release timestamp (falling back to the date),
      // so multiple releases on the same day still order correctly. Returning 0 for
      // true ties keeps the sort stable instead of shuffling equal-dated releases.
      [...(data.releases || [])].sort((a, b) => {
        const ka = a.releasedAt || a.date;
        const kb = b.releasedAt || b.date;
        if (ka < kb) return 1;
        if (ka > kb) return -1;
        return 0;
      }),
    []
  );
  const unreleased = data.unreleased || [];
  const latest = releases.length ? releases[0] : null;
  const latestVersion = appVersion || (latest ? latest.version : null);
  const latestDate = latest ? formatDate(latest.date) : null;
  const latestRelative = latest ? formatRelative(latest.releasedAt || latest.date) : null;
  const hasContent = unreleased.length > 0 || releases.length > 0;

  // Paginate the releases — only RELEASES_PER_PAGE are shown at a time. The
  // "Coming soon" (unreleased) block belongs with the newest entries, so it
  // appears on the first page only.
  const totalPages = Math.max(1, Math.ceil(releases.length / RELEASES_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedReleases = releases.slice(
    safePage * RELEASES_PER_PAGE,
    safePage * RELEASES_PER_PAGE + RELEASES_PER_PAGE
  );

  const openPopup = () => {
    setPage(0);
    setShow(true);
  };

  return (
    <>
      <span
        className="whats-new-link text-gray-800 text-hover-primary fw-semibold d-inline-flex align-items-center"
        onClick={openPopup}
      >
        <KTIcon iconName="notification-on" className="fs-5 text-primary me-1" />
        Release Notes
        {latestRelative && (
          <span className="text-muted fw-normal fs-8 ms-2" title={`Last release ${latestDate} (v${latestVersion})`}>
            · {latestRelative}
          </span>
        )}
      </span>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        size="lg"
        dialogClassName="whats-new-modal"
      >
        <div className="whats-new-header d-flex align-items-center">
          <span className="whats-new-header__glow" />
          <div className="whats-new-header__icon me-4">
            <KTIcon iconName="notification-on" className="fs-1" />
          </div>
          <div className="flex-grow-1">
            <h2 className="whats-new-header__title mb-1">Release Notes</h2>
            <div className="whats-new-header__subtitle">
              {latest
                ? `Latest release ${latestDate}${latestRelative ? " · " + latestRelative : ""}`
                : "The latest improvements and fixes in RevCon Outpost"}
            </div>
          </div>
          {latestVersion && (
            <span className="whats-new-header__version me-3">v{latestVersion}</span>
          )}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShow(false)}
          />
        </div>

        <div className="whats-new-body">
          {!hasContent && (
            <div className="whats-new-empty">
              <KTIcon iconName="information-5" className="fs-3x text-gray-400 mb-3" />
              <div className="fw-semibold">No changes recorded yet.</div>
            </div>
          )}

          {unreleased.length > 0 && safePage === 0 && (
            <ReleaseBlock title="Coming soon" versionLabel="Unreleased" changes={unreleased} unreleased />
          )}

          {pagedReleases.map((r) => (
            <ReleaseBlock
              key={`${r.version}-${r.date}`}
              title={formatDate(r.date)}
              versionLabel={`v${r.version}`}
              changes={r.changes || []}
            />
          ))}
        </div>

        <div className="modal-footer border-0 pt-0 d-flex align-items-center justify-content-between">
          {totalPages > 1 ? (
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <KTIcon iconName="arrow-left" className="fs-6" />
              </button>
              <span className="text-muted fw-semibold fs-7">
                Page {safePage + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                <KTIcon iconName="arrow-right" className="fs-6" />
              </button>
            </div>
          ) : (
            <span />
          )}
          <button type="button" className="btn btn-light" onClick={() => setShow(false)}>
            Close
          </button>
        </div>
      </Modal>
    </>
  );
};

export { WhatsNew };
